<template>
    <div
        id="editor"
        class="col-sm-12 nopads"
    >
        <div v-if="inspectableUpdate" class="col-sm-12 editor-form">
            <h2 class="editor-header">{{ inspectableUpdate.id > 0 ? $t('inspectable_editor.edit_inspectable') : $t('inspectable_editor.add_inspectable')}}</h2>
            <div class="col-sm-12 form-data">
                <span class="span-title">{{ $t('inspectable_editor.name') }}</span>
                <input
                    id="name"
                    v-model="inspectableUpdate.name"
                    type="text"
                    class="form-control form-control-sm"
                />
                <span class="span-title">{{ $t('inspection_editor.inactive') }}</span>
                <div class="form-check form-check-sm">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="in_active"
                        v-model="inspectableUpdate.in_active"
                    />
                </div>
                <span class="span-title">
                  {{ $t('inspectable_editor.type') }}
                </span>
                <select
                    class="form-select"
                    v-model="inspectableUpdate.type"
                >
                    <option
                        v-for="(type, index) in inspectableTypes"
                        :key="type"
                        :value="index"
                    >
                        {{ $t(`inspectable.types.${type}`) }}
                    </option>
                </select>

                <template v-if="isInspectableType('number')">
                    <div class="form-check my-2">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            id="useMinNumberValue"
                            v-model="useMinNumberValue"
                        >
                        <label class="form-check-label ms-2" for="useMinNumberValue">
                            {{ $t('inspectable.number.use_min') }}
                        </label>
                    </div>
                    <input
                        v-if="useMinNumberValue && inspectableUpdate.range"
                        type="number"
                        step="any"
                        class="form-control"
                        :value="inspectableUpdate.range.min"
                        @change="e => inspectableUpdate.range.min = Number(e.target.value)"
                    />

                    <div class="form-check my-2">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            id="useMaxNumberValue"
                            v-model="useMaxNumberValue"
                        >
                        <label class="form-check-label ms-2" for="useMaxNumberValue">
                            {{ $t('inspectable.number.use_max') }}
                        </label>
                    </div>

                    <input
                        v-if="useMaxNumberValue && inspectableUpdate.range"
                        type="number"
                        step="any"
                        class="form-control"
                        :value="inspectableUpdate.range.max"
                        @change="e => inspectableUpdate.range.max = Number(e.target.value)"
                    />
                </template>

                <template v-if="inspectableUpdate && (isInspectableType('select_multi') || isInspectableType('select_single'))">
                    <span class="span-title">
                      {{ $t('inspectable.select.items') }}
                    </span>
                    <div
                        class="d-flex title"
                        v-for="(item, index) in inspectableUpdate.selectable_items"
                        :key="index"
                    >
                        <input type="text" class="form-control" v-model="item.name" />
                        <button class="btn btn-sm btn-danger" @click="removeSelectableItem(index)">
                            <i class="fa fa-times"/>
                        </button>
                    </div>
                    <div class="title">
                        <button class="btn btn-sm btn-success" @click="addSelectableItem">
                            <i class="fa fa-plus" />
                        </button>
                    </div>
                </template>

                <div class="col-sm-12 button-container">
                    <button class="btn btn-secondary form-button" @click.stop="$emit('close')">
                      {{ $t('common.cancel') }}
                    </button>
                    <button class="btn btn-success form-button" :disabled="loading" @click.stop="submitInspectable">
                      {{ $t('common.save') }}
                    </button>
                </div>
            </div>
        </div>
        <div
            v-if="loading"
            id="loader"
            class="spinner"
        />
    </div>
</template>

<script>
import {inspectionHelper} from '../../mixins/InspectionMixin'
import {restApi} from '../../mixins/RestApiMixin'
import {types} from "../../../modules/Inspectable";
import {inject} from "vue";

export default {
    name: 'InspectableEditor',
    mixins: [inspectionHelper, restApi],
    props: {
        inspectable: {
            type: Object,
            default: null
        },
    },
    data: function () {
        return {
            loading: false,
            company: null,
            companies: [],
            useMinNumberValue: this.inspectable.range != null && this.inspectable.range.min !== null,
            useMaxNumberValue: this.inspectable.range != null && this.inspectable.range.max !== null,
            prevMinNumberValue: 0,
            prevMaxNumberValue: 0,
            prevSelectableItems: [],
            inspectableTypes: types,
            inspectableUpdate: this.jsonToInspectable(this.inspectable)
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert')
        return { showAlert }
    },
    watch: {
        useMinNumberValue(useMinNumberValue) {
            if (useMinNumberValue) {
                this.inspectableUpdate.range = {
                    ...this.inspectableUpdate.range,
                    min: this.prevMinNumberValue
                }
            }
            else {
                this.prevMinNumberValue = this.inspectableUpdate.range.min;
                if (this.useMaxNumberValue) {
                    this.inspectableUpdate.range.min = undefined;
                }
                else {
                    this.inspectableUpdate.range = undefined;
                }
            }
        },
        useMaxNumberValue(useMaxNumberValue) {
            if (useMaxNumberValue) {
                this.inspectableUpdate.range = {
                    ...this.inspectableUpdate.range,
                    max: this.prevMaxNumberValue
                }
            }
            else {
                this.prevMaxNumberValue = this.inspectableUpdate.range.max;
                if (this.useMinNumberValue) {
                    this.inspectableUpdate.range.max = undefined;
                }
                else {
                    this.inspectableUpdate.range =  undefined
                }
            }
        },
        'inspectableUpdate.type': function (type) {
            if (['select_single', 'select_multi'].includes(types[type])) {
                if (this.inspectableUpdate.selectable_items != null) this.prevSelectableItems = this.inspectableUpdate.selectable_items;
                this.inspectableUpdate.selectable_items = [...this.prevSelectableItems];
            }
            else if (this.inspectableUpdate.selectable_items != null) {
                this.prevSelectableItems = this.inspectableUpdate.selectable_items;
                this.inspectableUpdate.selectable_items = undefined
            }
        },
    },
    methods: {
        isInspectableType(type) {
            return types[this.inspectableUpdate.type] === type
        },

        addSelectableItem() {
            this.inspectableUpdate.selectable_items.push({name: ""})
        },

        removeSelectableItem(index) {
            this.inspectableUpdate.selectable_items.splice(index, 1)
        },

        handleCompanies: function (response) {
            this.companies = response.data
            this.loading = false
        },

        setDefaultValues: function (inspectable) {
            if (inspectable.name === '') {
                this.showAlert(this.$t('inspectable_editor.invalid_name'))
                return undefined
            }
            return inspectable
        },

        submitInspectable: function () {
            var jsonInspectable = this.setDefaultValues(this.inspectableUpdate)
            if (jsonInspectable !== undefined) {
                this.loading = true
                if (this.inspectable.id < 1) {
                    this.restAdd(this.inspectableUrl, jsonInspectable, this.success, this.fail)
                } else {
                    this.restUpdate(this.inspectableUrl, jsonInspectable, this.success, this.fail)
                }
            }
        },

        success: function () {
            this.$emit('closeAndUpdate')
            this.loading = false
        },

        fail: function () {
            this.showAlert(this.$t('common.alert_update_failed'))
            this.loading = false
        }
    }
}
</script>
